import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'

interface Conversation {
  id: string
  visitor_id: string
  visitor_email: string
  visitor_name: string
  status: string
  messages: any[]
  extracted_data: any
  created_at: string
  last_message_at: string
  last_viewed_at: string | null
}

export const Conversations: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const limit = 20

  const { data, isLoading } = useQuery({
    queryKey: ['conversations', page, search, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams()
      params.append('page', String(page))
      params.append('limit', String(limit))
      if (search) params.append('search', search)
      if (statusFilter) params.append('status', statusFilter)
      const response = await api.get(`/conversations?${params.toString()}`)
      return response.data
    },
  })

  const conversations: Conversation[] = data?.data || []
  const meta = data?.meta || { total: 0, totalPages: 1 }

  const getDisplayName = (conversation: Conversation) => {
    const extracted = conversation.extracted_data || {}
    return (
      conversation.visitor_name ||
      extracted.name ||
      extracted.nombre ||
      extracted.full_name ||
      extracted.contact_name ||
      extracted.first_name
    )
  }

  const getDisplayEmail = (conversation: Conversation) => {
    const extracted = conversation.extracted_data || {}
    return (
      conversation.visitor_email ||
      extracted.email ||
      extracted.correo ||
      extracted.contact_email
    )
  }

  // Mirrors the unread-count endpoint: active conversations never viewed or
  // with new messages since the last view
  const isUnread = (conversation: Conversation) =>
    conversation.status === 'active' &&
    (!conversation.last_viewed_at ||
      new Date(conversation.last_viewed_at) < new Date(conversation.last_message_at))

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-500/20 text-green-400'
      case 'closed': return 'bg-gray-500/20 text-gray-400'
      case 'converted': return 'bg-blue-500/20 text-blue-400'
      case 'spam': return 'bg-red-500/20 text-red-400'
      default: return 'bg-gray-500/20 text-gray-400'
    }
  }

  return (
    <div>
      <h1 className="text-3xl font-bold mb-8">{t('conversations.title')}</h1>

      {/* Filters */}
      <div className="bg-[#12121a] border border-white/10 rounded-xl p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <input
            type="text"
            placeholder={t('conversations.searchPlaceholder')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            className="flex-1 px-4 py-2 bg-[#1a1a25] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#006cd4]"
          />
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
            className="px-4 py-2 bg-[#1a1a25] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#006cd4]"
          >
            <option value="">{t('conversations.allStatus')}</option>
            <option value="active">{t('conversations.active')}</option>
            <option value="closed">{t('conversations.closed')}</option>
            <option value="converted">{t('conversations.converted')}</option>
            <option value="spam">{t('conversations.spam')}</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#12121a] border border-white/10 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">{t('conversations.loading')}</div>
        ) : conversations.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <p className="text-lg mb-2">{t('conversations.noConversationsTitle')}</p>
            <p className="text-sm">{t('conversations.noConversationsDesc')}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('conversations.visitor')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('common.status')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('conversations.messages')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('conversations.lastActivity')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {conversations.map((conversation) => (
                    <tr
                      key={conversation.id}
                      className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                      onClick={() => navigate(`/conversations/${conversation.id}`)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="relative">
                            <div className="w-8 h-8 rounded-full bg-[#006cd4]/20 flex items-center justify-center text-[#006cd4] text-sm">
                              {getDisplayName(conversation)?.charAt(0) || getDisplayEmail(conversation)?.charAt(0) || '?'}
                            </div>
                            {isUnread(conversation) && (
                              <span
                                className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full"
                                title={t('conversations.unread')}
                              />
                            )}
                          </div>
                          <div>
                            <div className={isUnread(conversation) ? 'font-bold' : 'font-medium'}>
                              {getDisplayName(conversation) || t('common.anonymous')}
                            </div>
                            <div className="text-sm text-gray-500">{getDisplayEmail(conversation) || t('common.noEmail')}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(conversation.status)}`}>
                          {conversation.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-300">
                        {Array.isArray(conversation.messages) ? conversation.messages.length : 0}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-sm">
                        {conversation.last_message_at
                          ? new Date(conversation.last_message_at).toLocaleString()
                          : new Date(conversation.created_at).toLocaleString()
                        }
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/conversations/${conversation.id}`)
                          }}
                          className="px-3 py-1 text-sm bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                        >
                          {t('common.view')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-white/10">
              <div className="text-sm text-gray-400">
                {t('common.showingOf', { count: conversations.length, total: meta.total })}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 bg-white/5 hover:bg-white/10 rounded-lg disabled:opacity-30 transition-colors"
                >
                  {t('common.previous')}
                </button>
                <span className="px-3 py-1 text-sm">
                  {t('common.pageOf', { page, total: meta.totalPages || 1 })}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(meta.totalPages || 1, p + 1))}
                  disabled={page >= meta.totalPages}
                  className="px-3 py-1 bg-white/5 hover:bg-white/10 rounded-lg disabled:opacity-30 transition-colors"
                >
                  {t('common.next')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
